import { useEffect, useMemo, useRef, useState } from 'react'
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from '@tanstack/react-table'
import {
  ArrowDownUp,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Search,
} from 'lucide-react'
import { api, type Channel } from '@/lib/api'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const PAGE_SIZE = 50

interface ChannelPickerProps {
  selected: Set<string>
  onSelectionChange: (next: Set<string>) => void
}

type SortKey = 'title' | 'frequency_desc'
type Tab = 'all' | 'following'

function uploadsPerWeek(channel: Pick<Channel, 'recent_upload_count' | 'frequency_window_days'>): number | null {
  if (channel.recent_upload_count === null || channel.recent_upload_count === undefined) return null
  const window = channel.frequency_window_days || 30
  return (channel.recent_upload_count / window) * 7
}

function FrequencyBadge({ channel }: { channel: Channel }) {
  const perWeek = uploadsPerWeek(channel)
  if (perWeek === null) {
    return <span className="text-xs italic text-muted-foreground">measuring…</span>
  }
  if (perWeek < 0.25) {
    return <Badge variant="secondary">rare uploads</Badge>
  }
  const rounded = perWeek < 1 ? perWeek.toFixed(1) : Math.round(perWeek)
  const label = `~${rounded}/week`
  if (perWeek <= 2) return <Badge variant="success">{label}</Badge>
  if (perWeek <= 7) return <Badge variant="warning">{label}</Badge>
  return <Badge variant="destructive">{label}</Badge>
}

export function ChannelPicker({ selected, onSelectionChange }: ChannelPickerProps) {
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortKey>('frequency_desc')
  const [tab, setTab] = useState<Tab>('all')
  const [loading, setLoading] = useState(true)

  // ----- All-tab state: server-paginated, server-searched -----
  const [allRows, setAllRows] = useState<Channel[]>([])
  const [allTotal, setAllTotal] = useState(0)

  // ----- Cache: any channel we've seen via either tab. Powers the
  // selection-based Following view without re-fetching what we already have.
  const [cache, setCache] = useState<Map<string, Channel>>(new Map())

  // Set of IDs we've definitively confirmed don't exist (server returned 404
  // semantics for them). Stored to avoid re-requesting on every render.
  const missingIds = useRef<Set<string>>(new Set())

  // Debounce search input → applied query.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 250)
    return () => clearTimeout(t)
  }, [searchInput])

  // Reset to page 1 whenever the user changes tab/sort/search, so they don't
  // land on a non-existent page.
  useEffect(() => {
    setPage(1)
  }, [tab, sort])

  // ----- Fetch the All tab from the server when its inputs change -----
  useEffect(() => {
    if (tab !== 'all') return
    let cancelled = false
    setLoading(true)
    api
      .getChannels({ page, pageSize: PAGE_SIZE, q: search || undefined, sort, filter: 'all' })
      .then((res) => {
        if (cancelled) return
        setAllRows(res.channels)
        setAllTotal(res.total)
        setCache((prev) => {
          const next = new Map(prev)
          for (const c of res.channels) next.set(c.id, c)
          return next
        })
      })
      .catch((err) => console.error(err))
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [tab, page, search, sort])

  // ----- Hydrate cache for any selected IDs we don't have yet (Following tab) -----
  useEffect(() => {
    if (tab !== 'following') return
    const missing: string[] = []
    for (const id of selected) {
      if (!cache.has(id) && !missingIds.current.has(id)) missing.push(id)
    }
    if (missing.length === 0) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    api
      .lookupChannels(missing)
      .then((res) => {
        if (cancelled) return
        setCache((prev) => {
          const next = new Map(prev)
          for (const c of res.channels) next.set(c.id, c)
          return next
        })
        // Mark as missing any IDs we asked for but didn't get back.
        const returned = new Set(res.channels.map((c) => c.id))
        for (const id of missing) if (!returned.has(id)) missingIds.current.add(id)
      })
      .catch((err) => console.error(err))
      .finally(() => {
        if (cancelled) return
        setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [tab, selected, cache])

  // ----- Compute the rows that should render based on tab state -----
  const followingRowsFiltered = useMemo<Channel[]>(() => {
    if (tab !== 'following') return []
    const rows: Channel[] = []
    for (const id of selected) {
      const c = cache.get(id)
      if (c) rows.push(c)
    }
    // Client-side ILIKE-ish search across title/description.
    const q = search.toLowerCase()
    const filtered = q
      ? rows.filter((c) => c.title.toLowerCase().includes(q))
      : rows
    // Apply sort
    filtered.sort((a, b) => {
      if (sort === 'frequency_desc') {
        const av = a.recent_upload_count ?? -1
        const bv = b.recent_upload_count ?? -1
        if (av !== bv) return bv - av
      }
      return a.title.localeCompare(b.title)
    })
    return filtered
  }, [tab, selected, cache, search, sort])

  const followingTotal = followingRowsFiltered.length
  const followingPageRows = useMemo(
    () => followingRowsFiltered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [followingRowsFiltered, page]
  )

  const visibleRows = tab === 'all' ? allRows : followingPageRows
  const visibleTotal = tab === 'all' ? allTotal : followingTotal

  const toggleOne = (id: string) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    onSelectionChange(next)
  }

  const togglePage = (checked: boolean) => {
    const next = new Set(selected)
    for (const c of visibleRows) {
      if (checked) next.add(c.id)
      else next.delete(c.id)
    }
    onSelectionChange(next)
  }

  const pageAllSelected = visibleRows.length > 0 && visibleRows.every((c) => selected.has(c.id))
  const pageSomeSelected = !pageAllSelected && visibleRows.some((c) => selected.has(c.id))

  const columns = useMemo<ColumnDef<Channel>[]>(
    () => [
      {
        id: 'select',
        header: () => (
          <Checkbox
            checked={pageAllSelected ? true : pageSomeSelected ? 'indeterminate' : false}
            onCheckedChange={(v) => togglePage(v === true)}
            aria-label="Select all on page"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={selected.has(row.original.id)}
            onCheckedChange={() => toggleOne(row.original.id)}
            aria-label={`Select ${row.original.title}`}
          />
        ),
      },
      {
        id: 'channel',
        header: 'Channel',
        cell: ({ row }) => {
          const c = row.original
          return (
            <div className="flex items-center gap-3">
              <Avatar className="h-9 w-9 shrink-0">
                <AvatarImage src={c.thumbnail_url} />
                <AvatarFallback>{c.title[0]}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{c.title}</p>
                {c.topics && c.topics.length > 0 ? (
                  <div className="mt-0.5 flex flex-wrap gap-1">
                    {c.topics.slice(0, 3).map((t) => (
                      <span key={t} className="inline-block rounded bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                        {t}
                      </span>
                    ))}
                  </div>
                ) : c.description ? (
                  <p className="truncate text-xs text-muted-foreground">{c.description}</p>
                ) : null}
              </div>
            </div>
          )
        },
      },
      {
        id: 'frequency',
        header: () => (
          <button
            type="button"
            className="inline-flex items-center gap-1 hover:text-foreground"
            onClick={() => setSort((s) => (s === 'frequency_desc' ? 'title' : 'frequency_desc'))}
          >
            Activity <ArrowDownUp className="h-3 w-3" />
          </button>
        ),
        cell: ({ row }) => <FrequencyBadge channel={row.original} />,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selected, visibleRows, pageAllSelected, pageSomeSelected]
  )

  const table = useReactTable({
    data: visibleRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const totalPages = Math.max(1, Math.ceil(visibleTotal / PAGE_SIZE))
  const pageStart = visibleTotal === 0 ? 0 : (page - 1) * PAGE_SIZE + 1
  const pageEnd = Math.min(visibleTotal, page * PAGE_SIZE)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search your channels…"
            className="pl-8"
          />
        </div>
        <div className="flex items-center gap-1 rounded-md border p-0.5 text-xs">
          <button
            type="button"
            onClick={() => setTab('all')}
            className={`rounded px-2 py-1 transition-colors ${tab === 'all' ? 'bg-accent text-accent-foreground' : 'text-muted-foreground'}`}
          >
            All
          </button>
          <button
            type="button"
            onClick={() => setTab('following')}
            className={`rounded px-2 py-1 transition-colors ${tab === 'following' ? 'bg-accent text-accent-foreground' : 'text-muted-foreground'}`}
          >
            Following ({selected.size})
          </button>
        </div>
      </div>

      <div className="rounded-xl border bg-card">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id} className="hover:bg-transparent">
                {hg.headers.map((h) => (
                  <TableHead key={h.id} className={h.id === 'select' ? 'w-10 pl-3' : h.id === 'frequency' ? 'w-32 text-right pr-4' : ''}>
                    {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={`skel-${i}`}>
                  <TableCell className="pl-3"><div className="h-4 w-4 animate-pulse rounded bg-muted" /></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9 animate-pulse rounded-full bg-muted" />
                      <div className="space-y-1.5">
                        <div className="h-3 w-40 animate-pulse rounded bg-muted" />
                        <div className="h-2.5 w-56 animate-pulse rounded bg-muted" />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-right pr-4"><div className="ml-auto h-5 w-20 animate-pulse rounded-full bg-muted" /></TableCell>
                </TableRow>
              ))
            ) : table.getRowModel().rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-sm text-muted-foreground">
                  {tab === 'following' && selected.size === 0
                    ? 'No channels selected yet. Switch to All to pick some.'
                    : 'No channels match your search.'}
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => {
                const isSelected = selected.has(row.original.id)
                return (
                  <TableRow
                    key={row.id}
                    data-state={isSelected ? 'selected' : undefined}
                    onClick={() => toggleOne(row.original.id)}
                    className="cursor-pointer"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={cell.column.id === 'select' ? 'pl-3' : cell.column.id === 'frequency' ? 'text-right pr-4' : ''}
                        onClick={(e) => {
                          // Let the checkbox handle its own toggle without double-firing.
                          if (cell.column.id === 'select') e.stopPropagation()
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <p>
          {loading ? (
            <span className="inline-flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</span>
          ) : visibleTotal === 0 ? (
            <>0 channels</>
          ) : (
            <>Showing {pageStart}-{pageEnd} of {visibleTotal}</>
          )}
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </Button>
          <span className="tabular-nums">
            Page {page} / {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || loading}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  )
}
